import { useState } from 'react';
import { Money } from '@delivery/shared';
import { ApiFailure, api } from '../lib/api';
import { money } from '../lib/format';

/**
 * 改这一单的司机酬劳。
 *
 * ★ 现实里总有按规则算不出来的单:绕远路、等了一小时、帮着搬上楼。
 *   在这之前唯一的办法是改计价规则再重算 —— 而那会把**别的单**一起改掉。
 *
 * ★ 必须写原因,而且会进**订单时间轴**:司机第一个会问的是
 *   「为什么这单只有 5 块」,而他看得到的只有时间轴。
 */
export function PayoutEditor({
  orderId,
  payout,
  currency,
  canEdit,
  onSaved,
}: {
  orderId: string;
  payout: string;
  currency: string;
  canEdit: boolean;
  onSaved: () => Promise<void> | void;
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(() => Money.toMajorString(Money.minor(Number(payout))));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!canEdit) return null;

  const parsed = Money.tryFromMajor(text.trim());
  const changed = parsed != null && parsed !== Number(payout);

  async function save() {
    if (busy || parsed == null || reason.trim().length < 2) return;
    setBusy(true);
    setErr(null);
    try {
      await api(`/orders/${orderId}/payout`, {
        method: 'PATCH',
        body: { driverPayout: parsed, reason: reason.trim() },
      });
      setOpen(false);
      setReason('');
      await onSaved();
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : '改不了');
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="text-xs text-brand-600 hover:underline dark:text-brand-500"
      >
        改酬劳
      </button>
    );
  }

  return (
    <div className="mt-2 rounded-lg border border-slate-200 p-3 dark:border-slate-700">
      <div className="mb-2 text-sm text-slate-600 dark:text-slate-300">
        现在是 {money(payout, currency)}
      </div>
      <label className="block text-sm">
        <span className="text-slate-500">改成(元)</span>
        <input
          aria-label="司机酬劳"
          value={text}
          onChange={(e) => setText(e.target.value)}
          inputMode="decimal"
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono
                     dark:border-slate-600 dark:bg-slate-800"
        />
      </label>
      {/*
        ★ 原因必填,而且提示里给的是**真实的例子**。
          「备注」两个字换不来任何信息 —— 而三个月后对账的人
          要的正是「那天为什么多给了 8 块」。
      */}
      <label className="mt-2 block text-sm">
        <span className="text-slate-500">为什么改</span>
        <input
          aria-label="改酬劳的原因"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="比如:客户改地址多跑了 12 公里"
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm
                     dark:border-slate-600 dark:bg-slate-800"
        />
      </label>

      <p className="mt-2 text-xs text-slate-400">
        会记进这一单的时间轴 —— 司机看得到。已经结算过的单改不了,要补要扣挂到下一期
      </p>

      {err && <div className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{err}</div>}

      <div className="mt-2 flex gap-2">
        <button
          onClick={() => {
            setOpen(false);
            setErr(null);
          }}
          className="h-9 rounded-lg border border-slate-300 px-3 text-sm text-slate-600
                     dark:border-slate-600 dark:text-slate-300"
        >
          取消
        </button>
        <button
          onClick={() => void save()}
          disabled={busy || !changed || reason.trim().length < 2}
          className="h-9 rounded-lg bg-brand-600 px-3 text-sm font-medium text-white
                     disabled:opacity-40"
        >
          {busy ? '保存中…' : '保存'}
        </button>
      </div>
    </div>
  );
}
